import { useState } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import { Button, Dialog, Input, Select } from '@/design-system';
import { isStrongPassword } from '@/features/auth/components/passwordPolicy';
import { PasswordRequirements } from '@/features/auth/components/PasswordRequirements';
import type { CreateStaffUserData, StaffUser } from '../api/users.service';

interface UserCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  createUser: UseMutationResult<unknown, unknown, CreateStaffUserData, unknown>;
  currentUserRole: StaffUser['role'] | undefined;
}

const emptyForm = (): CreateStaffUserData => ({
  name: '',
  email: '',
  password: '',
  role: 'SUPPORT',
});

export function UserCreateDialog({ isOpen, onClose, createUser, currentUserRole }: UserCreateDialogProps) {
  const [formData, setFormData] = useState<CreateStaffUserData>(emptyForm());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const reset = () => {
    setFormData(emptyForm());
    setFormErrors({});
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Nome é obrigatório';
    else if (formData.name.length < 2) errors.name = 'Nome deve ter no mínimo 2 caracteres';
    if (!formData.email.trim()) errors.email = 'Email é obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Email inválido';
    if (!formData.password) errors.password = 'Senha é obrigatória';
    else if (!isStrongPassword(formData.password))
      errors.password = 'A senha não atende à política de segurança.';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreate = async () => {
    if (!validateForm()) return;
    try {
      await createUser.mutateAsync(formData);
      handleClose();
    } catch {
      /* toast in mutation */
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={handleClose} title="Novo Usuário" size="md" footer={(
      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={handleClose} disabled={createUser.isPending}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={() => void handleCreate()} isLoading={createUser.isPending}>
          Criar
        </Button>
      </div>
    )}>
      <div className="space-y-4">
        <Input
          label="Nome"
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          error={formErrors.name}
          required
        />
        <Input
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
          error={formErrors.email}
          required
        />
        <div>
          <Input
            label="Senha"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
            error={formErrors.password}
            required
          />
          <PasswordRequirements value={formData.password} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Perfil</label>
          <Select
            value={formData.role}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, role: e.target.value as CreateStaffUserData['role'] }))
            }
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="SUPPORT">Suporte</option>
            <option value="FINANCE">Financeiro</option>
            <option value="SALES">Vendas</option>
            {currentUserRole === 'SUPERADMIN' && <option value="SUPERADMIN">Super Admin</option>}
          </Select>
        </div>
      </div>
    </Dialog>
  );
}
